import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import { useRealtimeTables } from "../../lib/useRealtimeTable";
import { useTabParam } from "../../lib/useTabParam";
import { fmtUSD } from "../../lib/analytics";
import { formatSupabaseError } from "../../lib/formatSupabaseError";
import { calcScope } from "../../lib/scopeCalc";
import { tradeMoneyOf } from "../../lib/agreementPayload";
import { supplementEntryWarning } from "../../lib/supplementEntryGuard";
import DealLink from "../DealLink";
import { daysInStage } from "../../lib/dealAge";

/**
 * Invoice Report — spreadsheet-style invoice lifecycle board (Jul 12, 2026).
 *
 * Greg's ask: one live report showing where each invoice STARTED (initial
 * insurance numbers), what moved it (credits, supplements), and where it
 * LANDED (final invoice + payments collected + balance). Fields are live —
 * Shannon / Chad / Jennifer / execs can click a cell, type, and everything
 * downstream recomputes (initial invoice + Δ are derived through the shared
 * calcScope single source of truth, and final_invoice_amount edits fire the
 * existing recompute_deal_payment_summary trigger so balances update too).
 *
 * Deal set: install-agreement signed onward (signed flag OR any post-agreement
 * stage). Default filter hides closed deals; pills narrow to Builds /
 * Supps & Collections / Closed.
 *
 * Every edit writes a finance_audit_log row (action='invoice_report_edit',
 * is_financial=true, before/after jsonb) so changes are traceable.
 *
 * Live: subscribes to deals + deal_payments realtime (both confirmed in the
 * supabase_realtime publication) with an 800ms debounce.
 *
 * ⚠ Numeric columns arrive from PostgREST as STRINGS — always go through
 * num() (never truthiness) per the documented "$0.00 is truthy" bug class.
 */

// ── Deal set ──────────────────────────────────────────────────────────────
// Post-agreement stages: everything from build submission through close.
// A signed deal in an earlier stage (or a cancelled-after-signing deal)
// is caught by the install_agreement_signed arm of the .or() filter.
const POST_SIGN_STAGES = [
  "build_submitted",
  "schedule_build",
  "build_scheduled",
  "roof_complete",
  "trades_in_progress",
  "trades_complete",
  "ready_for_supps",
  "ready_for_updated_invoice",
  "supps_waiting_on_final_invoice",
  "supps_waiting_on_claims",
  "collecting",
  "collect_payment",
  "delinquent",
  "register_oc_warranty",
  "commission_ready",
  "commission_approved",
  "commission_paid",
  "closed_won",
] as const;

const CLOSED_STAGES = new Set(["closed_won", "closed_lost", "cancelled", "closed_inspection"]);

const BUILD_STAGES = new Set([
  "install_meeting",
  "build_submitted",
  "schedule_build",
  "build_scheduled",
  "roof_complete",
  "trades_in_progress",
  "trades_complete",
]);

const SUPPS_STAGES = new Set([
  "ready_for_supps",
  "ready_for_updated_invoice",
  "supps_waiting_on_final_invoice",
  "supps_waiting_on_claims",
  "collecting",
  "collect_payment",
  "delinquent",
  "register_oc_warranty",
  "commission_ready",
  "commission_approved",
  "commission_paid",
]);

// Lifecycle sort order (earlier = higher in the default sort)
const STAGE_ORDER: Record<string, number> = {};
[
  "install_meeting", ...POST_SIGN_STAGES, "closed_lost", "cancelled", "closed_inspection",
].forEach((s, i) => { STAGE_ORDER[s] = i; });

const STAGE_SHORT: Record<string, string> = {
  install_meeting: "Install Mtg",
  build_submitted: "Build Submitted",
  schedule_build: "Schedule Build",
  build_scheduled: "Build Scheduled",
  roof_complete: "Roof Complete",
  trades_in_progress: "Trades",
  trades_complete: "Trades Done",
  ready_for_supps: "Ready for Supps",
  ready_for_updated_invoice: "Updated Invoice",
  supps_waiting_on_final_invoice: "Supps → Final Inv",
  supps_waiting_on_claims: "Supps → Final Inv",
  collecting: "Collecting",
  collect_payment: "Collecting",
  delinquent: "Delinquent",
  register_oc_warranty: "Register Warranty",
  commission_ready: "Commission Ready",
  commission_approved: "Commission Appr.",
  commission_paid: "Commission Paid",
  closed_won: "Closed Won",
  closed_lost: "Closed Lost",
  cancelled: "Cancelled",
  closed_inspection: "Closed Inspection",
};

function stageChipClass(stage: string | null): string {
  if (!stage) return "bg-slate-100 text-slate-600";
  if (stage === "delinquent") return "bg-red-100 text-red-700";
  if (stage === "collecting" || stage === "collect_payment") return "bg-emerald-100 text-emerald-700";
  if (CLOSED_STAGES.has(stage)) return "bg-slate-200 text-slate-700";
  if (SUPPS_STAGES.has(stage)) return "bg-teal-100 text-teal-700";
  if (BUILD_STAGES.has(stage)) return "bg-amber-100 text-amber-700";
  return "bg-slate-100 text-slate-600";
}

// ── Row shape ─────────────────────────────────────────────────────────────
interface Row {
  id: string;
  homeowner_name: string | null;
  property_street: string | null;
  property_city: string | null;
  stage: string | null;
  days_in_stage: number | null;
  stage_entered_at: string | null;
  phase_entered_at: string | null;
  created_at: string | null;
  owner_id: string | null;
  split_rm_id: string | null;
  claim_number: string | null;
  insurance_company: string | null;
  install_agreement_signed: boolean | null;
  is_retail: boolean | null;
  acv_only: boolean | null;
  // scope inputs (editable)
  rcv_total: number | string | null;
  depreciation_total: number | string | null;
  deductible: number | string | null;
  pwi_code: number | string | null;
  acv_credit: number | string | null;
  dep_credit: number | string | null;
  misc_credit: number | string | null;
  non_commissionable: number | string | null;
  upgrades: number | string | null;
  retail_profit: number | string | null;
  at_cost_retail: number | string | null;
  supplements: number | string | null;
  supplement_invoice_amount: number | string | null;
  supplement_approved_amount: number | string | null;
  // invoice endpoints
  initial_invoice_amount: number | string | null;
  final_invoice_amount: number | string | null;
  // payments (derived caches maintained by triggers — read-only here)
  pre_build_amount_received: number | string | null;
  post_build_amount_received: number | string | null;
  pre_build_amount_in_flight: number | string | null;
  post_build_amount_in_flight: number | string | null;
  balance_written_off: number | string | null;
  post_build_balance_remaining: number | string | null;
  // retail extras consumed by calcScope
  retail_contract_total: number | string | null;
  retail_squares: number | string | null;
  retail_base_per_square: number | string | null;
  retail_at_cost_total: number | string | null;
  retail_items: unknown;
  punch_list_items: unknown;
  // Oct 29, 2026 — trades billed to the homeowner from the trade sheet. Derived
  // by SQL trade_sync_deal (never edited here, so read-only in the report);
  // calcScope adds trade_ho_total to the initial invoice.
  trade_ho_total: number | string | null;
  trade_ho_floor: number | string | null;
  trade_margin_loss: number | string | null;
  trade_ho_lines: unknown;
  // Oct 30, 2026 (scope gate) — the carrier lines left with the homeowner. When
  // there are any, trigger deals_apply_scope_picks sets the ACV and DEP credits
  // from them on every write, so those two cells are read-only here.
  items_not_performing_detail: unknown;
}

/** The deal's credits come from picked carrier lines (the trigger's rule: a non-empty array). */
function creditsFromPicks(r: Pick<Row, "items_not_performing_detail">): boolean {
  return Array.isArray(r.items_not_performing_detail) && r.items_not_performing_detail.length > 0;
}

const PICKED_CREDITS_NOTE =
  "Set by the carrier lines left with the homeowner. Change those lines on the deal's scope check, not here.";

const SELECT_COLS =
  "id,homeowner_name,property_street,property_city,stage,days_in_stage,stage_entered_at,phase_entered_at,created_at,owner_id,split_rm_id," +
  "claim_number,insurance_company,install_agreement_signed,is_retail,acv_only," +
  "rcv_total,depreciation_total,deductible,pwi_code," +
  "acv_credit,dep_credit,misc_credit,non_commissionable," +
  "upgrades,retail_profit,at_cost_retail,supplements," +
  "supplement_invoice_amount,supplement_approved_amount," +
  "initial_invoice_amount,final_invoice_amount," +
  "pre_build_amount_received,post_build_amount_received," +
  "pre_build_amount_in_flight,post_build_amount_in_flight," +
  "balance_written_off,post_build_balance_remaining," +
  "retail_contract_total,retail_squares,retail_base_per_square,retail_at_cost_total,retail_items," +
  "punch_list_items," +
  "trade_ho_total,trade_ho_floor,trade_margin_loss,trade_ho_lines," +
  "items_not_performing_detail";

interface PaymentRow {
  id: string;
  deal_id: string;
  slot: string;
  method: string | null;
  kind: string | null;
  amount: number | string | null;
  state: string;
  collected_at: string | null;
  deposited_at: string | null;
  bank_cleared_at: string | null;
  stripe_cleared_at: string | null;
  iink_cleared_at: string | null;
  created_at: string | null;
}

// PostgREST returns numeric columns as strings — never rely on truthiness.
function num(v: number | string | null | undefined): number {
  if (v == null) return 0;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** Editable columns — the live scope/credit/supplement inputs. */
type EditableCol =
  | "rcv_total" | "depreciation_total" | "deductible" | "pwi_code"
  | "acv_credit" | "dep_credit" | "misc_credit" | "non_commissionable"
  | "upgrades" | "retail_profit" | "at_cost_retail"
  | "supplement_invoice_amount" | "supplement_approved_amount"
  | "final_invoice_amount";

const COL_LABELS: Record<EditableCol, string> = {
  rcv_total: "RCV",
  depreciation_total: "Depreciation",
  deductible: "Deductible",
  pwi_code: "PWI",
  acv_credit: "ACV Credit",
  dep_credit: "DEP Credit",
  misc_credit: "Misc Credit",
  non_commissionable: "Non-Collect",
  upgrades: "Upgrades",
  retail_profit: "Retail Profit",
  at_cost_retail: "At-Cost Retail",
  supplement_invoice_amount: "Supp Requested",
  supplement_approved_amount: "Supp Approved",
  final_invoice_amount: "Final Invoice",
};

type LifecycleFilter = "open" | "builds" | "supps" | "closed" | "all";
type SortKey = "lifecycle" | "balance" | "final" | "initial" | "name" | "days";

const SORT_LABELS: Record<SortKey, string> = {
  lifecycle: "Lifecycle stage",
  balance: "Balance (high → low)",
  final: "Final invoice (high → low)",
  initial: "Initial invoice (high → low)",
  name: "Homeowner A → Z",
  days: "Days in stage (high → low)",
};

const PAYMENT_STATE_DONE = new Set(["bank_cleared", "cleared"]);
const PAYMENT_STATE_DEAD = new Set(["voided", "refunded", "disputed"]);

function paymentStateChip(state: string): string {
  if (PAYMENT_STATE_DONE.has(state)) return "bg-emerald-100 text-emerald-700";
  if (PAYMENT_STATE_DEAD.has(state)) return "bg-red-100 text-red-700";
  return "bg-amber-100 text-amber-700";
}

function fmtDay(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// ── Inline editable money cell ────────────────────────────────────────────
function MoneyCell({
  value,
  onCommit,
  editable,
  strong,
  tone,
}: {
  value: number | string | null;
  onCommit?: (next: number) => void;
  editable: boolean;
  strong?: boolean;
  tone?: "credit" | "supp" | "final";
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const cancelRef = useRef(false);
  const v = num(value);

  const toneClass =
    tone === "credit" ? "text-rose-600" :
    tone === "supp" ? "text-teal-700" :
    tone === "final" ? "text-bardin-ink" : "text-slate-700";

  if (editing) {
    return (
      <input
        autoFocus
        inputMode="decimal"
        className="w-[92px] text-right text-[12px] font-medium border border-bardin-orange rounded px-1 py-0.5 tabular-nums bg-white outline-none"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          if (e.key === "Escape") { cancelRef.current = true; (e.target as HTMLInputElement).blur(); }
        }}
        onBlur={() => {
          setEditing(false);
          if (cancelRef.current) { cancelRef.current = false; return; }
          const cleaned = draft.replace(/[$,\s]/g, "");
          if (cleaned === "") return;
          const n = Number(cleaned);
          if (!Number.isFinite(n) || n === v) return;
          onCommit?.(n);
        }}
      />
    );
  }

  const display = v === 0 ? "—" : fmtUSD(v);
  return (
    <button
      type="button"
      disabled={!editable}
      onClick={() => { if (editable) { setDraft(v === 0 ? "" : String(v)); setEditing(true); } }}
      title={editable ? "Click to edit" : undefined}
      className={
        "w-full text-right tabular-nums text-[12px] px-1 py-0.5 rounded whitespace-nowrap " +
        (v === 0 ? "text-slate-300 " : toneClass + " ") +
        (strong ? "font-semibold " : "") +
        (editable
          ? "hover:bg-orange-50 hover:ring-1 hover:ring-bardin-orange/40 cursor-text"
          : "cursor-default")
      }
    >
      {display}
    </button>
  );
}

/** Read-only derived cell. */
function CalcCell({ value, strong, tone }: { value: number; strong?: boolean; tone?: "pos" | "neg" | "delta" }) {
  const cls =
    tone === "neg" ? "text-red-600" :
    tone === "pos" ? "text-emerald-700" :
    tone === "delta" ? (value > 0 ? "text-teal-700" : value < 0 ? "text-red-600" : "text-slate-300") :
    "text-slate-700";
  const display =
    tone === "delta"
      ? (value === 0 ? "—" : (value > 0 ? "+" : "−") + fmtUSD(Math.abs(value)))
      : (value === 0 ? "—" : fmtUSD(value));
  return (
    <div className={"text-right tabular-nums text-[12px] px-1 py-0.5 whitespace-nowrap " + (value === 0 && tone !== "delta" ? "text-slate-300 " : cls + " ") + (strong ? "font-semibold" : "")}>
      {display}
    </div>
  );
}

// ── Main tab ──────────────────────────────────────────────────────────────
export default function InvoiceReportTab() {
  const { member } = useAuth();
  const [rows, setRows] = useState<Row[]>([]);
  const [payments, setPayments] = useState<Record<string, PaymentRow[]>>({});
  const [roster, setRoster] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // Checked jobs — when any are checked, the KPI tiles + totals row reflect
  // ONLY the checked jobs (Greg: "checkboxes to filter specific jobs and
  // that filters the totals").
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useTabParam<LifecycleFilter>("ir", "open", ["open", "builds", "supps", "closed", "all"]);
  const [sortKey, setSortKey] = useState<SortKey>("lifecycle");
  const [reloadKey, setReloadKey] = useState(0);

  const load = useCallback(async () => {
    try {
      // Paginated fetch — never trust a bare .limit() on a growing table.
      const all: Row[] = [];
      const PAGE = 1000;
      for (let from = 0; from < 20000; from += PAGE) {
        const { data, error } = await supabase
          .from("deals")
          .select(SELECT_COLS)
          .or(`install_agreement_signed.eq.true,stage.in.(${POST_SIGN_STAGES.join(",")})`)
          // id tiebreaker → deterministic page boundaries (created_at can tie)
          .order("created_at", { ascending: false })
          .order("id", { ascending: true })
          .range(from, from + PAGE - 1);
        if (error) throw error;
        const page = (data ?? []) as unknown as Row[];
        all.push(...page);
        if (page.length < PAGE) break;
      }

      const [pay, team] = await Promise.all([
        supabase
          .from("deal_payments")
          .select("id,deal_id,slot,method,kind,amount,state,collected_at,deposited_at,bank_cleared_at,stripe_cleared_at,iink_cleared_at,created_at")
          .order("created_at", { ascending: true }),
        supabase.from("team_roster").select("id,full_name"),
      ]);
      if (pay.error) throw pay.error;

      const byDeal: Record<string, PaymentRow[]> = {};
      for (const p of (pay.data ?? []) as unknown as PaymentRow[]) {
        (byDeal[p.deal_id] ??= []).push(p);
      }
      const names: Record<string, string> = {};
      for (const t of (team.data ?? []) as Array<{ id: string; full_name: string | null }>) {
        names[t.id] = t.full_name ?? "";
      }

      setRows(all);
      setPayments(byDeal);
      setRoster(names);
      setErr(null);
    } catch (e) {
      setErr(formatSupabaseError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load, reloadKey]);

  // Live refresh — deals + deal_payments are both in supabase_realtime.
  // Debounced so a burst of trigger-driven updates collapses to one refetch.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useRealtimeTables(["deals", "deal_payments"], () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setReloadKey((k) => k + 1), 800);
  });

  // ── Inline edit commit: optimistic update + audit log ──
  const commitField = useCallback(async (dealId: string, col: EditableCol, next: number) => {
    const row = rows.find((r) => r.id === dealId);
    if (!row) return;
    // The trigger would put the picked lines' totals back, so the edit wouldn't stick (second review, Oct 30, 2026).
    if ((col === "acv_credit" || col === "dep_credit") && creditsFromPicks(row)) {
      setErr(`${COL_LABELS[col]} for ${row.homeowner_name ?? "this deal"}: ${PICKED_CREDITS_NOTE}`);
      return;
    }
    const before = num(row[col]);
    // optimistic
    setRows((prev) => prev.map((r) => (r.id === dealId ? { ...r, [col]: next } : r)));
    const { error } = await supabase.from("deals").update({ [col]: next }).eq("id", dealId);
    if (error) {
      setRows((prev) => prev.map((r) => (r.id === dealId ? { ...r, [col]: before } : r)));
      setErr(`Save failed — ${formatSupabaseError(error)}`);
      return;
    }
    setErr(null);
    setSavedFlash(`${COL_LABELS[col]} updated for ${row.homeowner_name ?? "deal"} — ${fmtUSD(next)}`);
    setTimeout(() => setSavedFlash(null), 2500);
    // Audit trail — best-effort, never blocks the save.
    try {
      await supabase.from("finance_audit_log").insert({
        deal_id: dealId,
        actor_id: member?.id ?? null,
        action: "invoice_report_edit",
        amount_delta: next - before,
        before_jsonb: { field: col, value: before },
        after_jsonb: { field: col, value: next, source: "invoice_report" },
        is_financial: true,
      });
    } catch { /* audit failure shouldn't block the edit */ }
  }, [rows, member?.id]);

  // ── Derived per-row calcs ──
  const computed = useMemo(() => {
    return rows.map((r) => {
      const calc = calcScope(r as unknown as Record<string, unknown>);
      const received = num(r.pre_build_amount_received) + num(r.post_build_amount_received);
      const inFlight = num(r.pre_build_amount_in_flight) + num(r.post_build_amount_in_flight);
      const writtenOff = num(r.balance_written_off);
      const finalInv = r.final_invoice_amount == null ? null : num(r.final_invoice_amount);
      const balance = r.post_build_balance_remaining == null ? null : num(r.post_build_balance_remaining);
      const suppEffective = calc.supplements; // approved only — a pending request is 0 (Sep 15, 2026)
      const delta = finalInv != null ? finalInv - calc.initialInvoice : 0;
      const trades = tradeMoneyOf(calc);
      return { row: r, calc, received, inFlight, writtenOff, finalInv, balance, suppEffective, delta, trades };
    });
  }, [rows]);

  // ── Filters + search + sort ──
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = computed.filter((c) => {
      const st = c.row.stage ?? "";
      if (filter === "open" && CLOSED_STAGES.has(st)) return false;
      if (filter === "builds" && !BUILD_STAGES.has(st)) return false;
      if (filter === "supps" && !SUPPS_STAGES.has(st)) return false;
      if (filter === "closed" && !CLOSED_STAGES.has(st)) return false;
      if (q) {
        const hay = `${c.row.homeowner_name ?? ""} ${c.row.property_street ?? ""} ${c.row.property_city ?? ""} ${c.row.claim_number ?? ""} ${c.row.insurance_company ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const byName = (a: typeof list[number], b: typeof list[number]) =>
      (a.row.homeowner_name ?? "").localeCompare(b.row.homeowner_name ?? "");
    list = [...list].sort((a, b) => {
      switch (sortKey) {
        case "balance": return (b.balance ?? -1) - (a.balance ?? -1) || byName(a, b);
        case "final": return (b.finalInv ?? -1) - (a.finalInv ?? -1) || byName(a, b);
        case "initial": return b.calc.initialInvoice - a.calc.initialInvoice || byName(a, b);
        case "name": return byName(a, b);
        case "days": return daysInStage(b.row) - daysInStage(a.row) || byName(a, b);
        case "lifecycle":
        default:
          return (STAGE_ORDER[a.row.stage ?? ""] ?? 99) - (STAGE_ORDER[b.row.stage ?? ""] ?? 99) || byName(a, b);
      }
    });
    return list;
  }, [computed, filter, search, sortKey]);

  // ── Effective set for totals: checked jobs when any are checked, else all visible ──
  const effective = useMemo(
    () => (selected.size > 0 ? visible.filter((c) => selected.has(c.row.id)) : visible),
    [visible, selected],
  );
  // When a selection is active, `effective` IS the selected∩visible set.
  const selectedVisibleCount = selected.size > 0 ? effective.length : 0;

  // ── KPI + per-column totals over the effective set ──
  const totals = useMemo(() => {
    const s = {
      rcv: 0, dep: 0, acv: 0, deductible: 0, pwi: 0,
      acvCr: 0, depCr: 0, miscCr: 0,
      upgrades: 0, retailProfit: 0, atCost: 0, tradesHo: 0, nonCollect: 0,
      initial: 0, suppInv: 0, suppAppr: 0, supp: 0, final: 0, delta: 0,
      received: 0, inFlight: 0, writtenOff: 0, balance: 0,
    };
    for (const c of effective) {
      const r = c.row;
      s.rcv += num(r.rcv_total);
      s.dep += num(r.depreciation_total);
      s.acv += c.calc.acv;
      s.deductible += num(r.deductible);
      s.pwi += num(r.pwi_code);
      s.acvCr += num(r.acv_credit);
      s.depCr += num(r.dep_credit);
      s.miscCr += num(r.misc_credit);
      s.upgrades += num(r.upgrades);
      s.retailProfit += num(r.retail_profit);
      s.atCost += num(r.at_cost_retail);
      s.tradesHo += c.trades.total;
      s.nonCollect += num(r.non_commissionable);
      s.initial += c.calc.initialInvoice;
      s.suppInv += num(r.supplement_invoice_amount);
      s.suppAppr += num(r.supplement_approved_amount);
      s.supp += c.suppEffective;
      s.final += c.finalInv ?? c.calc.initialInvoice;
      s.delta += c.delta;
      s.received += c.received;
      s.inFlight += c.inFlight;
      s.writtenOff += c.writtenOff;
      if (c.balance != null) s.balance += c.balance;
    }
    return { ...s, count: effective.length };
  }, [effective]);

  const toggleSelect = (id: string) =>
    setSelected((prev) => {
      const nx = new Set(prev);
      if (nx.has(id)) nx.delete(id); else nx.add(id);
      return nx;
    });

  const allVisibleSelected = visible.length > 0 && visible.every((c) => selected.has(c.row.id));
  const toggleSelectAllVisible = () =>
    setSelected((prev) => {
      if (allVisibleSelected) {
        const nx = new Set(prev);
        visible.forEach((c) => nx.delete(c.row.id));
        return nx;
      }
      const nx = new Set(prev);
      visible.forEach((c) => nx.add(c.row.id));
      return nx;
    });

  const toggleExpand = (id: string) =>
    setExpanded((prev) => {
      const nx = new Set(prev);
      if (nx.has(id)) nx.delete(id); else nx.add(id);
      return nx;
    });

  const rmName = (r: Row) => {
    const owner = r.owner_id ? roster[r.owner_id] : "";
    const first = owner ? owner.split(" ")[0] : "—";
    const split = r.split_rm_id && roster[r.split_rm_id] ? ` / ${roster[r.split_rm_id].split(" ")[0]}` : "";
    return first + split;
  };

  const FILTER_PILLS: Array<{ key: LifecycleFilter; label: string }> = [
    { key: "open", label: "Open" },
    { key: "builds", label: "Builds" },
    { key: "supps", label: "Supps & Collections" },
    { key: "closed", label: "Closed" },
    { key: "all", label: "All" },
  ];

  const N_COLS = 25; // total columns incl. expander — keep in sync with the header

  if (loading) {
    return <div className="text-sm text-slate-500 py-12 text-center">Loading invoice report…</div>;
  }

  return (
    <div>
      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-4">
        <Kpi
          label="Deals"
          value={String(totals.count)}
          sub={selected.size > 0 ? "✓ checked jobs only" : (filter === "open" ? "open lifecycle" : FILTER_PILLS.find((f) => f.key === filter)?.label ?? "")}
        />
        <Kpi label="Initial invoiced" value={fmtUSD(totals.initial, { short: true })} sub="scope-derived" />
        <Kpi label="Supplements" value={fmtUSD(totals.supp, { short: true })} sub="approved only" valueClass="text-teal-700" />
        <Kpi label="Final invoiced" value={fmtUSD(totals.final, { short: true })} sub="Shannon's final where set" />
        <Kpi label="Collected" value={fmtUSD(totals.received, { short: true })} sub="cleared payments" valueClass="text-emerald-600" />
        <Kpi label="Outstanding" value={fmtUSD(totals.balance, { short: true })} sub="balance remaining" valueClass={totals.balance > 0 ? "text-red-600" : "text-emerald-600"} />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex gap-1 flex-wrap">
          {FILTER_PILLS.map((p) => (
            <button
              key={p.key}
              onClick={() => setFilter(p.key)}
              className={
                "px-3 py-1.5 rounded-full text-[12px] font-medium border transition " +
                (filter === p.key
                  ? "bg-bardin-orange text-white border-bardin-orange"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")
              }
            >
              {p.label}
            </button>
          ))}
        </div>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, street, claim #, carrier…"
          className="flex-1 min-w-[200px] max-w-xs text-[13px] border border-slate-200 rounded-lg px-3 py-1.5 outline-none focus:border-bardin-orange"
        />
        <label className="text-[12px] text-slate-500 flex items-center gap-1.5">
          Sort
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value as SortKey)}
            className="text-[12px] border border-slate-200 rounded-lg px-2 py-1.5 bg-white outline-none"
          >
            {(Object.keys(SORT_LABELS) as SortKey[]).map((k) => (
              <option key={k} value={k}>{SORT_LABELS[k]}</option>
            ))}
          </select>
        </label>
        <span className="text-[11px] text-slate-400 flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block animate-pulse" />
          Live · click any orange-ringed cell to edit
        </span>
        {selected.size > 0 && (
          <span className="text-[11px] font-semibold text-bardin-orange bg-orange-50 border border-orange-200 rounded-full px-2.5 py-1 flex items-center gap-1.5">
            ✓ {selected.size} job{selected.size !== 1 ? "s" : ""} checked
            {selectedVisibleCount !== selected.size ? ` (${selectedVisibleCount} in view)` : ""} — totals reflect checked only
            <button onClick={() => setSelected(new Set())} className="text-slate-400 hover:text-red-600 font-bold" title="Clear selection">
              ✕
            </button>
          </span>
        )}
      </div>

      {err && (
        <div className="mb-3 text-[12px] text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {err}
        </div>
      )}
      {savedFlash && (
        <div className="mb-3 text-[12px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
          ✓ {savedFlash}
        </div>
      )}

      {/* Spreadsheet */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-auto max-h-[calc(100vh-170px)]">
        <table className="min-w-[1900px] w-full border-collapse">
          <thead>
            {/* Group header */}
            <tr className="text-[10px] uppercase tracking-wide text-slate-400">
              {/* colSpan=1 so the pinned region in this row matches the pinned
                  homeowner column below — a colSpan=2 sticky cell pins wider
                  than the body column and looks glitchy mid-scroll. */}
              <th className="sticky left-0 top-0 z-40 bg-slate-50 h-7 px-2 py-0 text-left shadow-[inset_0_-1px_0_#e2e8f0,inset_-1px_0_0_#e2e8f0]">Deal</th>
              <th className="sticky top-0 z-30 bg-slate-50 h-7 px-2 py-0 shadow-[inset_0_-1px_0_#e2e8f0]"> </th>
              <th className="sticky top-0 z-30 bg-blue-50 h-7 px-2 py-0 text-center shadow-[inset_0_-1px_0_#e2e8f0]" colSpan={5}>Initial Insurance Scope</th>
              <th className="sticky top-0 z-30 bg-rose-50 h-7 px-2 py-0 text-center shadow-[inset_0_-1px_0_#e2e8f0]" colSpan={3}>Credits</th>
              <th className="sticky top-0 z-30 bg-amber-50 h-7 px-2 py-0 text-center shadow-[inset_0_-1px_0_#e2e8f0]" colSpan={5}>Homeowner Adds</th>
              <th className="sticky top-0 z-30 bg-teal-50 h-7 px-2 py-0 text-center shadow-[inset_0_-1px_0_#e2e8f0]" colSpan={5}>Invoice Path</th>
              <th className="sticky top-0 z-30 bg-emerald-50 h-7 px-2 py-0 text-center shadow-[inset_0_-1px_0_#e2e8f0]" colSpan={5}>Payments</th>
            </tr>
            {/* Column header */}
            <tr className="text-[10px] uppercase tracking-wide text-slate-500">
              <Th sticky>
                <span className="flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    className="accent-[#f46530] cursor-pointer"
                    title={allVisibleSelected ? "Uncheck all in view" : "Check all in view"}
                  />
                  Homeowner
                </span>
              </Th>
              <Th>Stage</Th>
              <Th right>RCV</Th>
              <Th right>Dep</Th>
              <Th right>ACV</Th>
              <Th right>Deductible</Th>
              <Th right>PWI</Th>
              <Th right>ACV Cr</Th>
              <Th right>DEP Cr</Th>
              <Th right>Misc Cr</Th>
              <Th right>Upgrades</Th>
              <Th right>Retail Profit</Th>
              <Th right>At-Cost</Th>
              <Th right>Trades (HO)</Th>
              <Th right>Non-Collect</Th>
              <Th right>Initial Inv</Th>
              <Th right>Supp Requested</Th>
              <Th right>Supp Approved</Th>
              <Th right>Final Inv</Th>
              <Th right>Δ Initial→Final</Th>
              <Th right>Received</Th>
              <Th right>In Flight</Th>
              <Th right>Written Off</Th>
              <Th right>Balance</Th>
              <Th> </Th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr>
                <td colSpan={N_COLS} className="text-center text-[13px] text-slate-400 py-10">
                  No deals match this filter.
                </td>
              </tr>
            )}
            {visible.map((c) => {
              const r = c.row;
              const isRetail = r.is_retail === true;
              const scopeEditable = !isRetail; // retail jobs price off the retail contract, not the claim scope
              const picked = creditsFromPicks(r);
              const isOpen = expanded.has(r.id);
              const pays = payments[r.id] ?? [];
              return (
                <FragmentRow key={r.id}>
                  <tr className="border-t border-slate-100 hover:bg-slate-50/60 group">
                    {/* Deal */}
                    <td className={"sticky left-0 z-10 px-2 py-1.5 min-w-[210px] max-w-[250px] shadow-[inset_-1px_0_0_#f1f5f9] " + (selected.has(r.id) ? "bg-orange-50 group-hover:bg-orange-50" : "bg-white group-hover:bg-slate-50")}>
                      <div className="flex items-start gap-1.5">
                        <input
                          type="checkbox"
                          checked={selected.has(r.id)}
                          onChange={() => toggleSelect(r.id)}
                          className="accent-[#f46530] cursor-pointer mt-0.5 flex-shrink-0"
                          title="Include this job in the totals"
                        />
                        <div className="min-w-0">
                          <DealLink id={r.id} className="text-[13px] font-semibold block truncate">
                            {r.homeowner_name ?? "(no name)"}
                          </DealLink>
                          <div className="text-[10.5px] text-slate-400 truncate">
                            {r.property_street ?? "—"}{r.property_city ? `, ${r.property_city}` : ""}
                          </div>
                          <div className="text-[10.5px] text-slate-400 truncate">
                            RM {rmName(r)}
                            {r.insurance_company ? ` · ${r.insurance_company}` : ""}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      <span className={"inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold " + stageChipClass(r.stage)}>
                        {STAGE_SHORT[r.stage ?? ""] ?? r.stage ?? "—"}
                      </span>
                      <div className="flex gap-1 mt-0.5">
                        {isRetail && <span className="text-[9px] font-semibold text-purple-600 bg-purple-50 rounded px-1">🏷 RETAIL</span>}
                        {r.acv_only === true && <span className="text-[9px] font-semibold text-amber-700 bg-amber-50 rounded px-1">ACV ONLY</span>}
                        {daysInStage(r) > 0 && <span className="text-[9px] text-slate-400">{daysInStage(r)}d</span>}
                      </div>
                    </td>
                    {/* Insurance scope */}
                    <td className="px-1"><MoneyCell value={r.rcv_total} editable={scopeEditable} onCommit={(v) => commitField(r.id, "rcv_total", v)} /></td>
                    <td className="px-1"><MoneyCell value={r.depreciation_total} editable={scopeEditable} onCommit={(v) => commitField(r.id, "depreciation_total", v)} /></td>
                    <td className="px-1"><CalcCell value={c.calc.acv} /></td>
                    <td className="px-1"><MoneyCell value={r.deductible} editable={scopeEditable} onCommit={(v) => commitField(r.id, "deductible", v)} /></td>
                    <td className="px-1"><MoneyCell value={r.pwi_code} editable={scopeEditable} onCommit={(v) => commitField(r.id, "pwi_code", v)} /></td>
                    {/* Credits */}
                    <td className="px-1" title={picked ? PICKED_CREDITS_NOTE : undefined}><MoneyCell value={r.acv_credit} tone="credit" editable={scopeEditable && !picked} onCommit={(v) => commitField(r.id, "acv_credit", v)} /></td>
                    <td className="px-1" title={picked ? PICKED_CREDITS_NOTE : undefined}><MoneyCell value={r.dep_credit} tone="credit" editable={scopeEditable && !picked} onCommit={(v) => commitField(r.id, "dep_credit", v)} /></td>
                    <td className="px-1"><MoneyCell value={r.misc_credit} tone="credit" editable onCommit={(v) => commitField(r.id, "misc_credit", v)} /></td>
                    {/* HO adds */}
                    <td className="px-1"><MoneyCell value={r.upgrades} editable={scopeEditable} onCommit={(v) => commitField(r.id, "upgrades", v)} /></td>
                    <td className="px-1"><MoneyCell value={r.retail_profit} editable={scopeEditable} onCommit={(v) => commitField(r.id, "retail_profit", v)} /></td>
                    <td className="px-1"><MoneyCell value={r.at_cost_retail} editable={scopeEditable} onCommit={(v) => commitField(r.id, "at_cost_retail", v)} /></td>
                    {/* Oct 29, 2026 — trades billed to the homeowner, from the
                        trade sheet (read-only: change them on the trade sheet). */}
                    <td className="px-1" title={c.trades.lines.map((t) => `${t.label}: ${fmtUSD(t.amount)}`).join("\n") || undefined}>
                      <CalcCell value={c.trades.total} />
                      {c.trades.total > 0 && num(r.at_cost_retail) > 0 && !isRetail && (
                        <div className="text-[9px] text-amber-600 text-right pr-1" title="At-cost retail is set too — check the same gutter work isn't billed twice">⚠ + at-cost</div>
                      )}
                    </td>
                    <td className="px-1"><MoneyCell value={r.non_commissionable} editable onCommit={(v) => commitField(r.id, "non_commissionable", v)} /></td>
                    {/* Invoice path */}
                    <td className="px-1 bg-teal-50/30"><CalcCell value={c.calc.initialInvoice} strong /></td>
                    <td className="px-1">
                      <MoneyCell value={r.supplement_invoice_amount} tone="supp" editable onCommit={(v) => commitField(r.id, "supplement_invoice_amount", v)} />
                      {c.calc.supplementsPending && <div className="text-[9px] text-amber-600 text-right pr-1">⏳ pending</div>}
                      {(() => {
                        const w = supplementEntryWarning(num(r.supplement_invoice_amount), num(r.rcv_total));
                        return w ? (
                          <div className="text-[9px] text-red-600 text-right pr-1" title={w.message}>⚠ looks like a total</div>
                        ) : null;
                      })()}
                    </td>
                    <td className="px-1"><MoneyCell value={r.supplement_approved_amount} tone="supp" editable onCommit={(v) => commitField(r.id, "supplement_approved_amount", v)} /></td>
                    <td className="px-1 bg-teal-50/30">
                      <MoneyCell value={r.final_invoice_amount} tone="final" strong editable onCommit={(v) => commitField(r.id, "final_invoice_amount", v)} />
                    </td>
                    <td className="px-1"><CalcCell value={c.delta} tone="delta" /></td>
                    {/* Payments */}
                    <td className="px-1"><CalcCell value={c.received} tone="pos" /></td>
                    <td className="px-1">
                      <CalcCell value={c.inFlight} />
                      {c.inFlight > 0 && <div className="text-[9px] text-amber-600 text-right pr-1">processing</div>}
                    </td>
                    <td className="px-1"><CalcCell value={c.writtenOff} tone={c.writtenOff > 0 ? "neg" : undefined} /></td>
                    <td className="px-1">
                      {c.balance == null
                        ? <div className="text-right text-[11px] text-slate-300 px-1">no invoice</div>
                        : <CalcCell value={c.balance} strong tone={c.balance > 0 ? "neg" : "pos"} />}
                      {c.balance != null && c.balance === 0 && <div className="text-[9px] text-emerald-600 text-right pr-1">✓ paid</div>}
                    </td>
                    <td className="px-1 text-center">
                      <button
                        onClick={() => toggleExpand(r.id)}
                        className="text-slate-400 hover:text-bardin-orange text-[12px] px-1"
                        title={pays.length ? `${pays.length} payment${pays.length !== 1 ? "s" : ""}` : "No payments recorded"}
                      >
                        {isOpen ? "▾" : "▸"}{pays.length > 0 ? <span className="text-[9px] align-top ml-0.5">{pays.length}</span> : null}
                      </button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-slate-50/70">
                      <td className="sticky left-0 z-10 bg-slate-50 px-2 py-2 shadow-[inset_-1px_0_0_#f1f5f9] text-[10px] uppercase tracking-wide text-slate-400 align-top">
                        Payment history
                      </td>
                      <td colSpan={N_COLS - 1} className="px-3 py-2">
                        {pays.length === 0 ? (
                          <div className="text-[12px] text-slate-400">No payments recorded on this deal yet.</div>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {pays.map((p) => {
                              const when = p.bank_cleared_at ?? p.stripe_cleared_at ?? p.iink_cleared_at ?? p.deposited_at ?? p.collected_at ?? p.created_at;
                              return (
                                <div key={p.id} className="bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-[11px] flex items-center gap-2">
                                  <span className="font-semibold tabular-nums">{fmtUSD(num(p.amount))}</span>
                                  <span className="text-slate-400">{p.slot === "pre_build" ? "Pre-build" : p.slot === "post_build" ? "Post-build" : p.slot}</span>
                                  {p.method && <span className="text-slate-400">· {p.method}</span>}
                                  <span className={"px-1.5 py-0.5 rounded text-[9.5px] font-semibold " + paymentStateChip(p.state)}>
                                    {p.state.replace(/_/g, " ")}
                                  </span>
                                  {when && <span className="text-slate-400">{fmtDay(when)}</span>}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </FragmentRow>
              );
            })}
          </tbody>
          {/* Σ Totals — sticky footer. Sums the checked jobs when any are
              checked, otherwise everything matching the current filter. */}
          <tfoot>
            <tr className="text-[12px] font-bold">
              <SumTd sticky first>
                Σ {selected.size > 0 ? `${totals.count} checked` : `${totals.count} deal${totals.count !== 1 ? "s" : ""}`}
              </SumTd>
              <SumTd>{selected.size > 0 ? "checked only" : ""}</SumTd>
              <SumTd num v={totals.rcv} />
              <SumTd num v={totals.dep} />
              <SumTd num v={totals.acv} />
              <SumTd num v={totals.deductible} />
              <SumTd num v={totals.pwi} />
              <SumTd num v={totals.acvCr} cls="text-rose-600" />
              <SumTd num v={totals.depCr} cls="text-rose-600" />
              <SumTd num v={totals.miscCr} cls="text-rose-600" />
              <SumTd num v={totals.upgrades} />
              <SumTd num v={totals.retailProfit} />
              <SumTd num v={totals.atCost} />
              <SumTd num v={totals.tradesHo} />
              <SumTd num v={totals.nonCollect} />
              <SumTd num v={totals.initial} cls="text-bardin-ink" />
              <SumTd num v={totals.suppInv} cls="text-teal-700" />
              <SumTd num v={totals.suppAppr} cls="text-teal-700" />
              <SumTd num v={totals.final} cls="text-bardin-ink" />
              <SumTd num v={totals.delta} signed cls={totals.delta > 0 ? "text-teal-700" : totals.delta < 0 ? "text-red-600" : "text-slate-400"} />
              <SumTd num v={totals.received} cls="text-emerald-700" />
              <SumTd num v={totals.inFlight} cls="text-amber-600" />
              <SumTd num v={totals.writtenOff} cls={totals.writtenOff > 0 ? "text-red-600" : "text-slate-400"} />
              <SumTd num v={totals.balance} cls={totals.balance > 0 ? "text-red-600" : "text-emerald-700"} />
              <SumTd>{""}</SumTd>
            </tr>
          </tfoot>
        </table>
      </div>

      <p className="text-[11px] text-slate-400 mt-2">
        ACV, Initial Invoice, Δ, and Balance are derived live from the shared scope math — edit the inputs and they recompute.
        Trades (HO) are the trades billed to the homeowner from the trade sheet (due at completion, in the initial invoice); change them on the trade sheet.
        Received / In-flight / Balance come from the payment tracker. Retail deals price off their retail contract, so claim-scope
        cells are read-only there. Every edit is recorded in the finance audit log.
      </p>
    </div>
  );
}

// Small helpers ------------------------------------------------------------
function FragmentRow({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

function Th({ children, right, sticky }: { children: ReactNode; right?: boolean; sticky?: boolean }) {
  return (
    <th
      className={
        "sticky top-7 px-2 py-1.5 font-semibold whitespace-nowrap bg-white " +
        (right ? "text-right " : "text-left ") +
        (sticky
          ? "left-0 z-40 shadow-[inset_0_-2px_0_#e2e8f0,inset_-1px_0_0_#f1f5f9]"
          : "z-30 shadow-[inset_0_-2px_0_#e2e8f0]")
      }
    >
      {children}
    </th>
  );
}

function SumTd({
  children, v, num: isNum, signed, cls, sticky, first,
}: {
  children?: ReactNode; v?: number; num?: boolean; signed?: boolean; cls?: string; sticky?: boolean; first?: boolean;
}) {
  let body: ReactNode = children;
  if (isNum) {
    const val = v ?? 0;
    body = val === 0
      ? <span className="text-slate-300">—</span>
      : (signed ? (val > 0 ? "+" : "−") + fmtUSD(Math.abs(val)) : fmtUSD(val));
  }
  return (
    <td
      className={
        "sticky bottom-0 z-20 bg-slate-100 px-1 py-2 whitespace-nowrap tabular-nums shadow-[inset_0_2px_0_#e2e8f0] " +
        (isNum ? "text-right pr-2 " : "text-left px-2 ") +
        (cls ? cls + " " : "text-slate-700 ") +
        (sticky ? "left-0 z-40 " : "") +
        (first ? "text-[11px] uppercase tracking-wide" : "")
      }
    >
      {body}
    </td>
  );
}

function Kpi({ label, value, sub, valueClass }: { label: string; value: string; sub?: string; valueClass?: string }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl px-3 py-2.5">
      <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{label}</div>
      <div className={"text-lg font-bold tabular-nums " + (valueClass ?? "text-bardin-ink")}>{value}</div>
      {sub && <div className="text-[10px] text-slate-400">{sub}</div>}
    </div>
  );
}
